import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortDown,
  faSortUp,
  faUnlink
} from '@fortawesome/free-solid-svg-icons';
import Flex from 'components/common/Flex';
import Pagination from 'components/common/Pagination';
import SoftBadge from 'components/common/SoftBadge';
import SimpleBarReact from 'simplebar-react';
import { toast } from 'react-toastify';
import _ from 'lodash';
import moment from 'moment';
import { showAlert } from 'stores/modal';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useApproveShareRequestMutation,
  useCancelShareRequestMutation,
  useListShareRequestsQuery,
  useRejectShareRequestMutation,
  useRemoveAssociationMutation
} from 'rtk/org-admin/share-request.slice';
import ShareRequestApproveModal from 'components/common/ShareRequestApproveModal';
import { buildShareRequestAccessPayload } from 'util/shareRequestAccess';

const AssociationsShareTab = ({
  direction,
  statusOptions,
  onLoadingChange
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortOrder, setSortOrder] = useState('CreatedDescending');
  const [hasUserSortedRequestedOn, setHasUserSortedRequestedOn] =
    useState(false);
  const [actioningId, setActioningId] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveRequestId, setApproveRequestId] = useState(null);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectRequestId, setRejectRequestId] = useState(null);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelRequestId, setCancelRequestId] = useState(null);
  const [cancelDisplayName, setCancelDisplayName] = useState(null);

  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [disconnectAssociationId, setDisconnectAssociationId] = useState(null);
  const [disconnectDisplayName, setDisconnectDisplayName] = useState(null);

  const indexStart = pageIndex * pageSize;
  const {
    data: shareRequestsRes,
    isFetching,
    isLoading,
    isError: shareRequestsLoadError,
    error: shareRequestsError,
    refetch: refetchShareRequests
  } = useListShareRequestsQuery({
    indexStart,
    maxResults: pageSize,
    sortOrder,
    direction,
    connectionsOnly: true,
    status: statusFilter
  });

  const [approveShareRequest] = useApproveShareRequestMutation();
  const [rejectShareRequest] = useRejectShareRequestMutation();
  const [cancelShareRequest] = useCancelShareRequestMutation();
  const [removeAssociation] = useRemoveAssociationMutation();

  const shareRequests = shareRequestsRes?.Objects || [];
  const totalRecords = shareRequestsRes?.TotalRecords ?? 0;
  const endOfResults = !!shareRequestsRes?.EndOfResults;
  const loading = isFetching || isLoading;
  const error = shareRequestsLoadError
    ? _.get(
        shareRequestsError,
        'Message',
        'Failed to load association requests'
      )
    : null;

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    setPageIndex(0);
  }, [pageSize]);

  const handleStatusFilter = value => {
    setStatusFilter(value);
    setPageIndex(0);
  };

  const toMomentFromNow = value => {
    if (!value) return '—';
    const m = moment(value);
    return m.isValid() ? m.fromNow(true) : value;
  };

  const showBadgeColor = journalAccess => {
    if (journalAccess === 'N/A') return 'danger';
    if (journalAccess === 'Limited') return 'warning';
    return 'success';
  };

  const handleView = row => {
    const userGuid =
      direction === 'RECEIVED'
        ? row?.FromUserMasterGuid || row?.FromUserGuid
        : row?.ToUserMasterGuid || row?.ToUserGuid;
    if (!userGuid) {
      toast.error('Unable to determine the notary GUID to view journals.');
      return;
    }
    navigate(`/admin/notaries/${userGuid}/entries`);
  };

  const openApproveModal = requestId => {
    if (!requestId || actioningId) return;
    setApproveRequestId(requestId);
    setShowApproveModal(true);
  };
  const closeApproveModal = () => {
    if (actioningId) return;
    setShowApproveModal(false);
    setApproveRequestId(null);
  };
  const handleApprove = ({ accessType, scopeFromDate, scopeToDate }) => {
    if (!approveRequestId) return;
    setActioningId(approveRequestId);
    const payload = buildShareRequestAccessPayload(accessType, {
      scopeFromDate,
      scopeToDate
    });

    approveShareRequest({ guid: approveRequestId, payload })
      .unwrap()
      .then(() => {
        toast.success('Request approved.');
        closeApproveModal();
      })
      .catch(err => {
        dispatch(
          showAlert(
            'Unable to Approve',
            err?.Message || 'Failed to approve this request.'
          )
        );
      })
      .finally(() => setActioningId(null));
  };

  const openRejectModal = requestId => {
    if (!requestId || actioningId) return;
    setRejectRequestId(requestId);
    setShowRejectModal(true);
  };
  const closeRejectModal = () => {
    if (actioningId) return;
    setShowRejectModal(false);
    setRejectRequestId(null);
  };
  const handleReject = () => {
    if (!rejectRequestId) return;
    setActioningId(rejectRequestId);
    rejectShareRequest(rejectRequestId)
      .unwrap()
      .then(() => {
        toast.success('Request rejected.');
        closeRejectModal();
      })
      .catch(err => {
        dispatch(
          showAlert(
            'Unable to Reject',
            err?.Message || 'Failed to reject this request.'
          )
        );
      })
      .finally(() => setActioningId(null));
  };

  const openCancelModal = (requestId, displayName) => {
    if (!requestId || actioningId) return;
    setCancelRequestId(requestId);
    setCancelDisplayName(displayName || null);
    setShowCancelModal(true);
  };
  const closeCancelModal = () => {
    if (actioningId) return;
    setShowCancelModal(false);
    setCancelRequestId(null);
    setCancelDisplayName(null);
  };
  const handleCancel = () => {
    if (!cancelRequestId) return;
    setActioningId(cancelRequestId);
    cancelShareRequest(cancelRequestId)
      .unwrap()
      .then(() => {
        toast.success('Request cancelled.');
        closeCancelModal();
      })
      .catch(err => {
        dispatch(
          showAlert(
            'Unable to Cancel',
            err?.Message || 'Failed to cancel this request.'
          )
        );
      })
      .finally(() => setActioningId(null));
  };

  const openDisconnectModal = (associationId, displayName) => {
    if (!associationId || actioningId) return;
    setDisconnectAssociationId(associationId);
    setDisconnectDisplayName(displayName || null);
    setShowDisconnectModal(true);
  };
  const closeDisconnectModal = () => {
    if (actioningId) return;
    setShowDisconnectModal(false);
    setDisconnectAssociationId(null);
    setDisconnectDisplayName(null);
  };
  const handleDisconnect = () => {
    if (!disconnectAssociationId) return;
    setActioningId(disconnectAssociationId);
    removeAssociation(disconnectAssociationId)
      .unwrap()
      .then(() => {
        toast.success('Disconnected.');
        closeDisconnectModal();
      })
      .catch(err => {
        dispatch(
          showAlert(
            'Unable to Disconnect',
            err?.Message || 'Failed to disconnect association.'
          )
        );
      })
      .finally(() => setActioningId(null));
  };

  const handleToggleRequestedOnSort = () => {
    setHasUserSortedRequestedOn(true);
    setSortOrder(current =>
      current === 'CreatedDescending' ? 'CreatedAscending' : 'CreatedDescending'
    );
    setPageIndex(0);
  };

  const nameLabel = direction === 'RECEIVED' ? 'From' : 'To';
  const emailLabel = direction === 'RECEIVED' ? 'From email' : 'To email';
  const dropdownId = `associations-${direction.toLowerCase()}-status-filter`;
  return (
    <>
      <ShareRequestApproveModal
        show={showApproveModal}
        onHide={closeApproveModal}
        onApprove={handleApprove}
        actioning={!!actioningId}
        accessTypeId={`associations-approve-access-${direction}`}
      />

      <Modal
        show={showRejectModal}
        onHide={() => !actioningId && closeRejectModal()}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Reject Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to reject this request?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeRejectModal}
            disabled={!!actioningId}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            disabled={!!actioningId}
          >
            {actioningId ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : null}
            Reject
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCancelModal}
        onHide={() => !actioningId && closeCancelModal()}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancel Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <>
            This will cancel the connection request for{' '}
            {cancelDisplayName || 'this notary'}.
            <br />
            You can send a new request later if needed.
          </>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeCancelModal}
            disabled={!!actioningId}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleCancel}
            disabled={!!actioningId}
          >
            {actioningId ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : null}
            Cancel Request
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDisconnectModal}
        onHide={() => !actioningId && closeDisconnectModal()}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Disconnect Association</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <>
            This will disconnect {disconnectDisplayName || 'this association'}.
            <br />
            They will no longer have access.
          </>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeDisconnectModal}
            disabled={!!actioningId}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDisconnect}
            disabled={!!actioningId}
          >
            {actioningId ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : null}
            Disconnect
          </Button>
        </Modal.Footer>
      </Modal>

      {error && !loading && (
        <div className="alert alert-warning mb-3">
          {error}
          <Button
            variant="link"
            size="sm"
            className="p-0 ms-2"
            onClick={() => refetchShareRequests()}
          >
            Retry
          </Button>
        </div>
      )}

      {!error && (
        <Dropdown align="end" className="filter-dropdown mb-1">
          <Dropdown.Toggle
            variant="outline-primary"
            size="sm"
            id={dropdownId}
            style={{ minWidth: '200px' }}
          >
            {statusFilter === 'ALL' ? 'Status: All' : `Status: ${statusFilter}`}
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="filter-dropdown-menu"
            style={{ minWidth: '200px' }}
          >
            {statusOptions
              .filter(s => s !== 'ALL')
              .map(option => (
                <Dropdown.Item
                  key={option}
                  className={
                    statusFilter === option
                      ? 'text-primary fw-semi-bold active'
                      : ''
                  }
                  onClick={() => handleStatusFilter(option)}
                >
                  {option}
                </Dropdown.Item>
              ))}
            <Dropdown.Divider />
            <Dropdown.Item
              eventKey="clear"
              onClick={() => handleStatusFilter('ALL')}
              className={
                statusFilter === 'ALL' ? 'text-primary fw-semi-bold' : ''
              }
            >
              Show All
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      )}

      {loading && !shareRequests.length && (
        <Flex justifyContent="center" className="py-3">
          <Spinner animation="border" size="sm" />
        </Flex>
      )}

      {!error && (
        <>
          {(shareRequests.length > 0 || pageIndex > 0 || totalRecords > 0) &&
            !isLoading && (
              <Pagination
                pageIndex={pageIndex}
                pageSize={pageSize}
                totalRecords={totalRecords}
                currentCount={shareRequests.length}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                endOfResults={endOfResults}
                disabled={loading}
                pageSizeOptions={[5, 10, 15, 25]}
              />
            )}

          {shareRequests.length !== 0 && !isLoading && (
            <SimpleBarReact>
              <Table
                responsive
                striped
                bordered
                className={`fs--1 mb-0 overflow-hidden${
                  loading ? ' refreshing-skeleton' : ''
                }`}
              >
                <thead className="bg-200 text-900 text-nowrap align-middle">
                  <tr>
                    <th className="border-0 fw-semi-bold">{nameLabel}</th>
                    <th className="border-0 fw-semi-bold">{emailLabel}</th>
                    <th className="border-0 fw-semi-bold d-none d-lg-table-cell">
                      Location
                    </th>
                    <th className="border-0 fw-semi-bold d-none d-lg-table-cell">
                      Journal access
                    </th>
                    <th className="border-0 fw-semi-bold d-none d-lg-table-cell">
                      Status
                    </th>
                    <th
                      className="border-0 fw-semi-bold d-none d-lg-table-cell cursor-pointer"
                      onClick={handleToggleRequestedOnSort}
                    >
                      Requested on
                      <FontAwesomeIcon
                        icon={
                          !hasUserSortedRequestedOn
                            ? faSort
                            : sortOrder === 'CreatedDescending'
                            ? faSortDown
                            : faSortUp
                        }
                        className="ms-1 text-500"
                      />
                    </th>
                    <th className="border-0 fw-semi-bold d-none d-lg-table-cell">
                      Expires on
                    </th>
                    <th className="border-0 fw-semi-bold d-none d-lg-table-cell">
                      Connected since
                    </th>
                    <th className="border-0 fw-semi-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shareRequests.map(row => {
                    const requestId =
                      row?.ShareRequestGUID ||
                      row?.RequestGUID ||
                      row?.GUID ||
                      row?.Id ||
                      row?.id ||
                      null;
                    const associationId =
                      row?.AssociationGUID ||
                      row?.AssociationId ||
                      requestId ||
                      null;
                    const rowKey =
                      requestId ||
                      `${row?.FromUserMasterGuid || 'x'}-${
                        row?.ToUserMasterGuid || 'y'
                      }-${row?.ReceivedDateTime || 't'}`;

                    const fromName =
                      row?.FromUserName || row?.RequesterName || '-';
                    const toName = row?.ToUserName || row?.Name || '-';
                    const name = direction === 'RECEIVED' ? fromName : toName;
                    const email =
                      direction === 'RECEIVED'
                        ? row?.FromUserEmail || row?.RequesterEmail || ''
                        : row?.ToUserEmail || '';

                    const status = String(
                      row?.Status ?? row?.status ?? ''
                    ).toUpperCase();
                    const isPending =
                      status === 'PENDING' || status === 'REQUESTED';
                    const isAccepted = status === 'ACCEPTED';

                    const requestedOn =
                      row?.ReceivedDateTime ??
                      row?.RequestedOn ??
                      row?.Created ??
                      row?.CreatedOn ??
                      '—';
                    const expiresOn = row?.ExpiresDateTime ?? '—';
                    const connectedSince = row?.connectedSince ?? '—';

                    const shouldShowCancel = direction === 'SENT' && isPending;
                    const shouldShowDisconnect = isAccepted;
                    const shouldShowApproveReject =
                      direction === 'RECEIVED' && isPending;

                    return (
                      <tr
                        key={rowKey}
                        className="align-middle white-space-nowrap"
                      >
                        <td className="align-middle py-2">{name}</td>
                        <td className="align-middle py-2">{email}</td>
                        <td className="align-middle py-2 d-none d-lg-table-cell">
                          {row?.Location || '—'}
                        </td>
                        <td className="align-middle py-2 d-none d-lg-table-cell">
                          <SoftBadge
                            bg={showBadgeColor(row?.JournalAccessRequested)}
                            pill
                          >
                            {row?.JournalAccessRequested || '—'}
                          </SoftBadge>
                        </td>
                        <td className="align-middle py-2 d-none d-lg-table-cell">
                          <SoftBadge
                            bg={
                              status === 'PENDING' || status === 'REQUESTED'
                                ? 'warning'
                                : status === 'ACCEPTED'
                                ? 'success'
                                : 'secondary'
                            }
                            pill
                          >
                            {status || '—'}
                          </SoftBadge>
                        </td>
                        <td className="align-middle py-2 d-none d-lg-table-cell">
                          {moment(requestedOn)
                            .local()
                            .format('MM/DD/YYYY h:mm A')}
                        </td>
                        <td className="align-middle py-2 d-none d-lg-table-cell">
                          {moment(expiresOn)
                            .local()
                            .format('MM/DD/YYYY h:mm A')}
                        </td>
                        <td className="align-middle py-2 d-none d-lg-table-cell">
                          {toMomentFromNow(connectedSince)}
                        </td>
                        <td className="align-middle py-2">
                          <Flex
                            justifyContent="end"
                            className="gap-2 flex-wrap"
                          >
                            {/* {isAccepted && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="px-2"
                                disabled={
                                  actioningId === requestId ||
                                  actioningId === associationId
                                }
                                onClick={() => handleView(row)}
                                title="View"
                                aria-label="View"
                              >
                                View
                              </Button>
                            )} */}

                            {shouldShowApproveReject && (
                              <>
                                <Button
                                  variant="success"
                                  size="sm"
                                  className="px-2"
                                  disabled={actioningId === requestId}
                                  onClick={() => openApproveModal(requestId)}
                                >
                                  {actioningId === requestId ? (
                                    <Spinner
                                      animation="border"
                                      size="sm"
                                      className="me-1"
                                    />
                                  ) : (
                                    'Approve'
                                  )}
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="px-2"
                                  disabled={actioningId === requestId}
                                  onClick={() => openRejectModal(requestId)}
                                >
                                  Reject
                                </Button>
                              </>
                            )}

                            {shouldShowCancel && (
                              <Button
                                variant="danger"
                                size="sm"
                                className="px-2"
                                disabled={actioningId === requestId}
                                onClick={() => openCancelModal(requestId, name)}
                                title="Cancel"
                                aria-label="Cancel"
                              >
                                {actioningId === requestId ? (
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-1"
                                  />
                                ) : (
                                  'Cancel'
                                )}
                              </Button>
                            )}

                            {shouldShowDisconnect && (
                              <Button
                                variant="danger"
                                size="sm"
                                className="px-2"
                                disabled={actioningId === associationId}
                                onClick={() =>
                                  openDisconnectModal(associationId, name)
                                }
                                title="Disconnect"
                                aria-label="Disconnect"
                              >
                                {actioningId === associationId ? (
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-1"
                                  />
                                ) : (
                                  <>
                                    <FontAwesomeIcon
                                      icon={faUnlink}
                                      className="me-1"
                                    />
                                    Disconnect
                                  </>
                                )}
                              </Button>
                            )}
                          </Flex>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </SimpleBarReact>
          )}

          {shareRequests.length === 0 && !loading && (
            <div className="text-center py-5 text-600 fs--1">
              No association requests found.
            </div>
          )}
        </>
      )}
    </>
  );
};

AssociationsShareTab.propTypes = {
  direction: PropTypes.oneOf(['SENT', 'RECEIVED']).isRequired,
  statusOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  onLoadingChange: PropTypes.func
};

export default AssociationsShareTab;
